import { css } from '@emotion/react';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Inline } from '@spark-web/inline';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLink, TextLinkButton } from '@spark-web/text-link';
import { FC } from 'react';

import { CheckIcon } from '~/components/common/icon';
import { useModal } from '~/hooks/useModal';
import { MarketplacePartner } from '~/types/marketplace';

const rebateInformation = ['Finding which rebates are available', 'Checking your eligibility', 'Helping you to apply'];

type VendorHighlightsProps = {
  partner?: MarketplacePartner;
};

export const VendorHighlights: FC<VendorHighlightsProps> = ({ partner }) => {
  const { isOpen, open, close, onCloseAutoFocus } = useModal('rebate-assistance');

  const options = [
    {
      label: 'Government rebate assistance available',
      action: (
        <Text size="xsmall" tone="info" css={css({ textDecoration: 'underline' })}>
          <TextLinkButton onClick={open}>Find out more</TextLinkButton>
        </Text>
      ),
    },
    {
      label: 'Available for site visits',
    },
  ];

  return (
    <>
      <Stack gap="medium">
        {options.map((option, i) => (
          <Box key={i} display="inline-flex" gap="small" alignItems="center">
            <CheckIcon partner={partner} />
            <Inline gap="xsmall">
              <Text size="xsmall">{option.label}</Text>
              {option?.action}
            </Inline>
          </Box>
        ))}
      </Stack>

      {/*
        TODO: modal header is missing, update spark-web to support icon/iconTitle in header
        https://brighte.atlassian.net/browse/ET-522
      */}
      <ContentDialog
        title="Government rebate assistance"
        showCloseButton
        isOpen={isOpen}
        onToggle={close}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <Stack gap="xlarge">
          <Text size="large" weight="bold">
            Government rebate assistance
          </Text>
          <Text size="xsmall">
            Your selected vendors will be able to guide you through the process of finding and applying for rebates.
          </Text>
          <Text size="xsmall" weight="bold">
            Assistance includes:
          </Text>
          <Stack gap="large">
            {rebateInformation.map((info, i) => (
              <Box key={i} display="flex" flexDirection="row" alignItems="center" gap="small">
                <CheckIcon partner={partner} />
                <Text size="xsmall">{info}</Text>
              </Box>
            ))}
          </Stack>
          <Text size="xsmall">
            You can discover which rebates and incentives are available to you on{' '}
            <TextLink href="https://www.energy.gov.au/rebates" tone="info" target="_blank">
              https://www.energy.gov.au/rebates
            </TextLink>
            .
          </Text>
          <Button tone="neutral" rounded onClick={close}>
            Back to Marketplace
          </Button>
        </Stack>
      </ContentDialog>
    </>
  );
};
